import { constructDefinitionSourceFromBase } from '../port/k1-compile/from-base/definition-source';
import { constructPlayerEncounterSourceFromBase } from './from-base-player-source';
import { constructClassificationFromBase } from './from-base-classification';
import { createClassificationContext } from './from-base-classification-context';
import { constructPlayerDefinitionPictures, constructEncounterSourcePictures } from './from-base-player-media';
import { readItemField } from './item-field-reader';
import { fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseInventoryMetadata } from './from-base-inventory';
import { finalizeInventoryDefinitionLayerSurface } from './from-base-inventory-layer-surface';

const FROM_BASE_DEFINITION_STAGES = ['ability-spell-finalization', 'catalog-lean-normalization'];
const FROM_BASE_CREATURE_STAGES = ['tiers-only-skills', 'status-environment-layering', 'inventory-construction-grants',
    'size-shapeshift-vitality', 'nested-grants-override-prune', 'integer-rollup-projection', 'full-skill-entry-projection',
    'feat-passive-defense-flags', 'root-tier-dedup-armor-class', 'defenses-flags-ac-capacity',
    ...FROM_BASE_DEFINITION_STAGES];

/** K1's later finalizer can call constructClassificationFromBase directly.
 * This adapter exposes the completed window while those later stages land. */
export function compileClassificationEntryFromBase(entity: 'definition' | 'creature', base: Row, metadata: FromBaseInventoryMetadata) {
    let row: Row | undefined;
    const completedStages: string[] = [], diagnostics: string[] = [];
    const missingStages = ['classification-template-swarm-media',
        ...(entity === 'definition' ? FROM_BASE_DEFINITION_STAGES : FROM_BASE_CREATURE_STAGES)];
    if (fromBaseRecord(base.player_characters)) return { row, completedStages,
        missingStages: ['definition-source-initialization', ...missingStages],
        diagnostics: ['definition-source-initialization:player-finalizer'] };
    try {
        const context = createClassificationContext(base, metadata);
        const template = fromBaseRecord(base.template) ? base.template : null;
        const templateId = template ? readItemField(template, 'item_id') : null;
        const source = template ? fromBaseUuid(templateId) ? context.rawDefinition(templateId) : null : base;
        if (!source) throw new FromBaseInputMissing('creature-template-definition', String(templateId ?? ''));
        row = constructDefinitionSourceFromBase(source);
        // A model donor finishes its ordinary layer mirror before the encounter
        // overlay. Preserve that authored bucket order without running its
        // context-dependent definition/cache producers ahead of creature rules.
        if (template && readItemField(source, 'item_type') === 'creature_model') {
            const layers = readItemField(finalizeInventoryDefinitionLayerSurface(row, metadata.definitionLayerOrder), 'item_layer_types');
            if (fromBaseRecord(layers)) row._from_base_template_layer_order = Object.fromEntries(Object.keys(layers).map(key => [key, true]));
        }
        if (fromBaseRecord(base.encounter_creatures)) {
            (row.items as Row).item_type = readItemField(base.encounter_creatures, 'item_type') === 'lair' ? 'lair' : 'creature';
            row.encounter_creatures = constructEncounterSourcePictures(constructPlayerEncounterSourceFromBase(
                base.encounter_creatures, fromBaseRecord(base.encounter_context) ? base.encounter_context : null,
                base.caster_summons as readonly Row[] | undefined),
                [...base.pictures as readonly Row[] ?? [], ...base.picture_sources as readonly Row[] ?? []], metadata.cdnUrl);
        }
        const pictures = constructPlayerDefinitionPictures(source, metadata.cdnUrl, context.readers.layerValue);
        if (pictures.length) row.pictures = { item: pictures };
        completedStages.push('definition-source-initialization');
        row = constructClassificationFromBase(base, metadata, row, context);
        diagnostics.push(...context.diagnostics);
        completedStages.push('classification-template-swarm-media');
        missingStages.shift();
        // query_full_item_arr dispatches finalize_creature_arr (and its ability
        // finalizer) only for creature items. Other definitions proceed to post.
        if (entity === 'definition' && readItemField(row, 'item_type') !== 'creature') {
            completedStages.push('ability-spell-finalization');
            missingStages.shift();
        }
    } catch (error) {
        if (!(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
        diagnostics.push(error.message);
        if (!completedStages.length) missingStages.unshift('definition-source-initialization');
    }
    return { row, completedStages, missingStages, diagnostics };
}
